// Brands table of the Product Brands screen: usage counts, active toggle,
// edit/delete. A flat single-list simplification of the attribute dictionary's
// values table — no drag-reorder (brands are ordered alphabetically by the BE)
// and no category bindings. Owns the edit/delete modals; creation is triggered
// from the screen header.

import { Icon } from 'design-system';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  HStack,
  IconButtonWithTooltip,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from 'design-system';
import {
  ProductBrand,
  ProductBrandUsage,
  usageForBrand,
  useDeleteProductBrandApi,
  useUpdateProductBrandApi,
} from 'apis/product-brands';
import { useState } from 'react';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import { BrandFormModal } from './brand-form-modal';

interface Props {
  brands: ProductBrand[];
  usage?: ProductBrandUsage[];
  isLoading: boolean;
}

export const BrandsTable = ({ brands, usage, isLoading }: Props) => {
  const toast = useToast();
  const [editBrand, setEditBrand] = useState<ProductBrand | null>(null);
  const [deleteBrand, setDeleteBrand] = useState<ProductBrand | null>(null);

  const updateApi = useUpdateProductBrandApi({
    onError: (error: any) => {
      // hooks toast server messages; this is the network-error fallback
      if (!error?.response?.data?.message) toast({ title: 'Failed to update brand', status: 'error' });
    },
  });

  const deleteApi = useDeleteProductBrandApi({
    onSuccess: () => {
      toast({ title: `Brand "${deleteBrand?.name}" deleted`, status: 'success' });
      setDeleteBrand(null);
    },
    onError: (error: any) => {
      if (!error?.response?.data?.message) toast({ title: 'Failed to delete brand', status: 'error' });
    },
  });

  const toggleActive = (brand: ProductBrand, isActive: boolean) => updateApi.mutate({ id: brand.id, data: { isActive } });

  return (
    <Box>
      <Table size='sm'>
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Usage</Th>
            <Th>Active</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {brands.map((brand) => {
            const usageCount = usageForBrand(usage, brand.name);
            return (
              <Tr key={brand.id}>
                <Td>{brand.name}</Td>
                <Td>
                  {usageCount > 0 ? (
                    <Tooltip
                      label={`used by ${usageCount} product${usageCount === 1 ? '' : 's'} — deactivate instead of delete`}>
                      <Badge colorScheme='orange' variant='subtle'>
                        {usageCount}
                      </Badge>
                    </Tooltip>
                  ) : (
                    <Text fontSize='xs' color='gray.400'>
                      0
                    </Text>
                  )}
                </Td>
                <Td>
                  <Switch
                    isChecked={brand.isActive}
                    onChange={(e) => toggleActive(brand, e.target.checked)}
                    colorScheme='green'
                  />
                </Td>
                <Td>
                  <HStack spacing='0'>
                    <IconButtonWithTooltip
                      size='sm'
                      label='Edit'
                      aria-label='edit-brand-btn'
                      Icon={<Icon as={FiEdit} />}
                      variant='unstyled'
                      onClick={() => setEditBrand(brand)}
                    />
                    <IconButtonWithTooltip
                      size='sm'
                      label='Delete'
                      aria-label='delete-brand-btn'
                      Icon={<Icon as={FiTrash2} />}
                      variant='unstyled'
                      onClick={() => setDeleteBrand(brand)}
                    />
                  </HStack>
                </Td>
              </Tr>
            );
          })}
          {brands.length === 0 && (
            <Tr>
              <Td colSpan={4}>
                <Text color='gray.500' fontSize='sm' textAlign='center' py='4'>
                  {isLoading ? 'Loading brands…' : 'No brands yet.'}
                </Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>

      {editBrand && (
        <BrandFormModal isOpen={!!editBrand} onClose={() => setEditBrand(null)} brand={editBrand} />
      )}

      <Modal isOpen={!!deleteBrand} onClose={() => setDeleteBrand(null)} size='md' closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Brand</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status='warning'>
              <AlertIcon />
              This action cannot be undone. Are you sure you want to delete “{deleteBrand?.name}”?
            </Alert>
          </ModalBody>
          <ModalFooter gap='2'>
            <Button variant='outline' onClick={() => setDeleteBrand(null)} isDisabled={deleteApi.isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              onClick={() => deleteBrand && deleteApi.mutate({ id: deleteBrand.id })}
              isLoading={deleteApi.isLoading}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
